import { Box, HBox, VBox } from "@ui/box/Box";
import Button from "@ui/button/Button";
import { Typo } from "@ui/text/Typo";
import { formatCurrencyWithNumberToSymbolWithNumber } from "@ui/utils/formatter";
import { ReactNode, useEffect, useRef, useState } from "react";
import { GD } from "src/GD";
import { USER_ROLE } from "src/services/AuthService";
import { Avatar } from "../Avatar";
import { ProfileSettings } from "../components/ProfileSettings";
import { S_UNIVERSAL_DIALOG } from "../components/universalDialog/signals";
import { OnboardingGate } from "./OnboardingGate";
import { PrivateCards } from "./pages/PrivateCards";
import { PrivateDashboard } from "./pages/PrivateDashboard";
import { PrivateTransactions } from "./pages/PrivateTransactions";
import { PrivateFunding } from "./pages/PrivateFunding";
import {
  PRIVATE_MOBILE_BREAKPOINT as MOBILE,
  PRIVATE_THEME as T,
  usePrivateSkin,
} from "./theme";
import { BrandLockup } from "./ui";
import { IssuerSwitcher } from "../issuer/IssuerSwitcher";
import { useIssuer } from "../issuer/IssuerProvider";
import { getIssuerIdentity } from "../issuer/issuerIdentity";

export type PrivateTab = "dashboard" | "cards" | "funding" | "transactions";

const NAV: { id: PrivateTab; label: string; icon: ReactNode }[] = [
  { id: "dashboard", label: "Overview", icon: <IconHome /> },
  { id: "cards", label: "My Cards", icon: <IconCard /> },
  { id: "funding", label: "Add funds", icon: <IconPlus /> },
  { id: "transactions", label: "Transactions", icon: <IconActivity /> },
];

const BALANCE_SYNC_INTERVAL_MS = 60_000;
const BALANCE_SYNC_WINDOW_MS = 900_000;

type TrackedDeposit = {
  status: ICryptoDeposit["status"];
  deadline: number;
};

export const PrivateView = () => {
  usePrivateSkin();
  const [tab, setTab] = useState<PrivateTab>("dashboard");
  const [profile, , setProfile] = GD.REQ_PROFILE.useRequest();
  const { selectedIssuer, scopeKey, refreshIssuers } = useIssuer();
  const [transactionCard, setTransactionCard] = useState<{ scopeKey: string; uid: string } | null>(null);
  const transactionCardUid = transactionCard?.scopeKey === scopeKey ? transactionCard.uid : undefined;
  const issuerIdentity = getIssuerIdentity(selectedIssuer?.issuer);
  const availableNav = selectedIssuer?.crypto_topup
    ? NAV
    : NAV.filter((item) => item.id !== "funding");
  const setProfileRef = useRef(setProfile);
  const trackedDepositsRef = useRef<Map<number, TrackedDeposit>>(new Map());
  const contentScrollRef = useRef<HTMLDivElement | null>(null);
  setProfileRef.current = setProfile;
  // `undefined` = still resolving; `null` = no onboarding block (ungated).
  const [onboarding, setOnboarding] = useState<IOnboarding | null | undefined>(
    undefined,
  );
  const navigateToTab = (nextTab: PrivateTab, cardUid?: string) => {
    setTransactionCard(cardUid ? { scopeKey, uid: cardUid } : null);
    setTab(nextTab);
    if (contentScrollRef.current) contentScrollRef.current.scrollTop = 0;
  };

  useEffect(() => {
    GD.REQ_ONBOARDING.request().then((o) => setOnboarding(o ?? null));
    const handler = GD.S_ONBOARDING_UPDATED.subscribe((o) =>
      setOnboarding(o ?? null),
    );
    return () => {
      GD.S_ONBOARDING_UPDATED.unsubscribe(handler);
    };
  }, []);

  useEffect(() => {
    const subscription = GD.S_PROFILE_UPDATED.subscribe(() => {
      void GD.REQ_PROFILE.request().then((nextProfile) =>
        setProfileRef.current(nextProfile),
      );
    });
    return () => {
      GD.S_PROFILE_UPDATED.unsubscribe(subscription);
    };
  }, []);

  // The backend's `required` flag follows our KYC. The legacy kyc_status now
  // mirrors the provider and must not keep a verified user outside the app.
  const gated = !!onboarding?.required;
  const privateAppReady = onboarding !== undefined && !gated;

  useEffect(() => {
    if (tab === "funding" && !selectedIssuer?.crypto_topup) navigateToTab("dashboard");
  }, [selectedIssuer?.crypto_topup, tab]);

  useEffect(() => {
    if (!privateAppReady) return;

    let alive = true;
    const refreshBalances = () =>
      Promise.allSettled([GD.REQ_COMPANY_REFRESH.request(), refreshIssuers()]);
    const syncRecentDeposits = async (refreshBalance: boolean) => {
      try {
        const response = await GD.REQ_CRYPTO_DEPOSITS.request();
        if (!alive || !response.data) return;

        const now = Date.now();
        const seenIds = new Set<number>();
        let shouldRefreshBalance = false;

        for (const deposit of response.data) {
          seenIds.add(deposit.id);
          const tracked = trackedDepositsRef.current.get(deposit.id);

          if (deposit.status === "pending") {
            const nextTracked = tracked ?? {
              status: "pending" as const,
              deadline: now + BALANCE_SYNC_WINDOW_MS,
            };
            trackedDepositsRef.current.set(deposit.id, {
              ...nextTracked,
              status: "pending",
            });
            if (refreshBalance && nextTracked.deadline > now)
              shouldRefreshBalance = true;
          } else if (
            deposit.status === "credited" &&
            tracked?.status === "pending"
          ) {
            shouldRefreshBalance = true;
            trackedDepositsRef.current.delete(deposit.id);
          } else {
            trackedDepositsRef.current.delete(deposit.id);
          }
        }

        for (const depositId of trackedDepositsRef.current.keys()) {
          if (!seenIds.has(depositId))
            trackedDepositsRef.current.delete(depositId);
        }

        if (shouldRefreshBalance) await refreshBalances();
      } catch {
        // Background synchronization is best-effort; visible actions report errors.
      }
    };
    const syncVisibleState = () => {
      if (document.visibilityState !== "visible") return;
      void refreshBalances();
      void syncRecentDeposits(false);
    };

    syncVisibleState();
    const timer = window.setInterval(() => {
      if (document.visibilityState === "visible") void syncRecentDeposits(true);
    }, BALANCE_SYNC_INTERVAL_MS);
    document.addEventListener("visibilitychange", syncVisibleState);

    return () => {
      alive = false;
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", syncVisibleState);
    };
  }, [privateAppReady, refreshIssuers, scopeKey]);

  if (onboarding === undefined) {
    // Brief flash while onboarding resolves; keep the private background.
    return (
      <Box sx={{ width: "100%", height: "100dvh", backgroundColor: T.appBg }} />
    );
  }

  if (gated) {
    return (
      <OnboardingGate
        onApproved={() => GD.REQ_ONBOARDING_SET_STATUS.request("approved")}
      />
    );
  }

  const openProfile = () => {
    if (!profile?.uid) return;
    S_UNIVERSAL_DIALOG.invoke({
      label: "Profile settings",
      size: "medium",
      content: <ProfileSettings showLogout />,
      closeDialog: () => S_UNIVERSAL_DIALOG.invoke(null),
    });
  };
  const profileInitials = (profile?.name || profile?.email || "P")
    .split(/\s+/)
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <Box
      data-workspace="private"
      sx={{
        display: "grid",
        gridTemplateColumns: "240px minmax(0, 1fr)",
        width: "100%",
        height: "100dvh",
        overflow: "hidden",
        background: T.appBg,
        fontFamily:
          "Euclid, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        [MOBILE]: {
          display: "block",
          height: "100dvh",
          background: T.appBg,
          position: "fixed",
          inset: 0,
        },
      }}
    >
      {/* Sidebar */}
      <VBox
        sx={{
          height: "100dvh",
          position: "relative",
          background: T.sidebarBg,
          borderRight: `1px solid ${T.border}`,
          padding:
            "calc(30px + var(--safe-area-inset-top, env(safe-area-inset-top, 0px))) 20px 22px",
          justifyContent: "space-between",
          overflow: "hidden",
          "&::after": {
            content: "''",
            position: "absolute",
            width: "240px",
            height: "240px",
            left: "-130px",
            bottom: "-105px",
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(43,42,40,.06), transparent 68%)",
            pointerEvents: "none",
          },
          [MOBILE]: { display: "none" },
        }}
      >
        <VBox gap="42px" sx={{ position: "relative", zIndex: 1 }}>
          <Box sx={{ padding: "0 8px" }}>
            <BrandLockup />
          </Box>

          <VBox gap="6px" role="tablist" aria-label="Private navigation">
            {availableNav.map((item) => {
              const active = tab === item.id;
              return (
                <Box
                  key={item.id}
                  element="button"
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => navigateToTab(item.id)}
                  sx={{
                    display: "flex",
                    width: "100%",
                    alignItems: "center",
                    gap: "13px",
                    padding: "12px 14px",
                    borderRadius: "13px",
                    border: "0",
                    cursor: "pointer",
                    color: active ? T.accentHover : T.sidebarMuted,
                    background: active
                      ? T.accentSoft
                      : "transparent",
                    fontWeight: active ? 700 : 500,
                    fontFamily: "inherit",
                    transition:
                      "background-color .18s ease, color .18s ease, transform .18s ease",
                    "&::before": {
                      content: "''",
                      width: active ? "3px" : 0,
                      height: "20px",
                      borderRadius: "3px",
                      backgroundColor: T.mint,
                      marginLeft: active ? "-7px" : "-3px",
                    },
                    "&:focus-visible": {
                      outline: `2px solid ${T.mint}`,
                      outlineOffset: "2px",
                    },
                    "&:hover": {
                      backgroundColor: active ? T.accentSoft : "rgba(43,42,40,0.05)",
                      color: active ? T.accentHover : T.text,
                      transform: "translateX(2px)",
                    },
                  }}
                >
                  <span style={{ display: "flex", lineHeight: 0 }}>
                    {item.icon}
                  </span>
                  <span
                    style={{
                      color: "inherit",
                      fontWeight: "inherit",
                      fontSize: "0.92rem",
                    }}
                  >
                    {item.label}
                  </span>
                </Box>
              );
            })}
          </VBox>
        </VBox>

        <VBox gap="14px" sx={{ position: "relative", zIndex: 1 }}>
          <Box
            sx={{
              padding: "14px",
              borderRadius: "15px",
              border: `1px solid ${T.border}`,
              backgroundColor: "#FFFFFF",
            }}
          >
            <Typo sx={{ color: T.text, fontSize: "0.78rem", fontWeight: 600 }}>
              Private workspace
            </Typo>
            <Typo
              sx={{
                color: T.sidebarMuted,
                fontSize: "0.7rem",
                marginTop: "3px",
                lineHeight: 1.35,
              }}
            >
              Manage cards, funding and account activity.
            </Typo>
          </Box>
          <Button
            variant="secondary"
            onClick={() => GD.S_AUTH_LOG_OUT.invoke()}
            sx={{
              width: "100%",
              justifyContent: "center",
              borderColor: T.border,
              color: T.text,
              backgroundColor: "#FFFFFF",
            }}
          >
            Log out
          </Button>
        </VBox>
      </VBox>

      {/* Main */}
      <VBox
        sx={{
          height: "100vh",
          overflow: "hidden",
          [MOBILE]: { height: "100dvh", backgroundColor: "transparent" },
        }}
      >
        <Box
          sx={{
            display: "contents",
            [MOBILE]: {
              display: "block",
              flexShrink: 0,
              margin:
                "calc(var(--safe-area-inset-top, env(safe-area-inset-top, 0px)) + 8px) 12px 8px",
              position: "relative",
              overflow: "hidden",
              borderRadius: "24px",
              border: `1px solid ${T.border}`,
              background:
                "#FFFFFF",
              backdropFilter: "blur(24px)",
              boxShadow:
                "0 1px 2px rgba(15,23,42,.04), 0 8px 24px rgba(15,23,42,.06)",
            },
          }}
        >
        {/* Header */}
        <HBox
          sx={{
            height:
              "calc(82px + var(--safe-area-inset-top, env(safe-area-inset-top, 0px)))",
            flexShrink: 0,
            padding:
              "var(--safe-area-inset-top, env(safe-area-inset-top, 0px)) 40px 0",
            alignItems: "center",
            justifyContent: "space-between",
            backgroundColor: "rgba(255,255,255,.72)",
            backdropFilter: "blur(18px)",
            borderBottom: "0",
            boxShadow: "0 1px 0 rgba(15,23,42,.04)",
            position: "relative",
            transition: "box-shadow .2s ease",
            "&::after": {
              content: '""',
              position: "absolute",
              left: "clamp(18px, 4vw, 40px)",
              right: "clamp(18px, 4vw, 40px)",
              bottom: 0,
              height: "1px",
              borderRadius: "999px",
              background: selectedIssuer
                ? `linear-gradient(90deg, transparent 0%, ${issuerIdentity.border}42 24%, ${issuerIdentity.accent}32 50%, ${issuerIdentity.border}42 76%, transparent 100%)`
                : "linear-gradient(90deg, transparent, rgba(15,23,42,.12), transparent)",
            },
            [MOBILE]: {
              height: "68px",
              padding: "0 12px",
              backgroundColor: "transparent",
              borderBottom: 0,
              boxShadow: "none",
            },
          }}
        >
          <VBox>
            <Box sx={{ display: "none", [MOBILE]: { display: "block" } }}>
              <BrandLockup compact />
            </Box>
            <Typo
              sx={{
                color: T.muted,
                fontSize: "0.8rem",
                fontWeight: 500,
                [MOBILE]: { display: "none" },
              }}
            >
              Private account
            </Typo>
            <Typo
              sx={{
                fontWeight: 700,
                fontSize: "1.22rem",
                letterSpacing: "-0.025em",
                color: T.text,
                [MOBILE]: { display: "none" },
              }}
            >
              {availableNav.find((n) => n.id === tab)?.label}
            </Typo>
          </VBox>

          <HBox
            gap="16px"
            sx={{ alignItems: "center", [MOBILE]: { gap: "7px" } }}
          >
            {selectedIssuer?.crypto_topup && <Button
              title="Add funds"
              variant="secondary"
              onClick={() => navigateToTab("funding")}
              sx={{
                color: "#fff",
                borderColor: T.accent,
                backgroundColor: T.accent,
                boxShadow: "0 4px 12px rgba(30, 58, 95,.3)",
                "&:hover": {
                  color: "#fff",
                  backgroundColor: T.accentHover,
                  borderColor: T.accentHover,
                  boxShadow: "0 6px 16px rgba(30, 58, 95,.36)",
                },
                "&:focus-visible": {
                  outline: `3px solid ${T.accentSoft}`,
                  outlineOffset: "2px",
                },
                [MOBILE]: { display: "none" },
              }}
            />}
            <Box sx={{ [MOBILE]: { display: "none" } }}>
              <IssuerSwitcher />
            </Box>
            <Button
              title="Log out"
              icon={<IconLogout />}
              ariaLabel="Mobile log out"
              variant="secondary"
              onClick={() => GD.S_AUTH_LOG_OUT.invoke()}
              sx={{
                display: "none",
                [MOBILE]: {
                  display: "flex",
                  height: "42px",
                  minWidth: "98px",
                  padding: "0 13px",
                  gap: "7px",
                  justifyContent: "center",
                  color: T.text,
                  fontSize: "0.78rem",
                  fontWeight: 750,
                  letterSpacing: "0.01em",
                  borderRadius: "999px",
                  borderColor: selectedIssuer
                    ? `${issuerIdentity.accent}2E`
                    : "rgba(15,23,42,.12)",
                  background:
                    "#FFFFFF",
                  boxShadow:
                    "0 1px 2px rgba(15,23,42,.06)",
                  transition:
                    "transform .18s ease, box-shadow .18s ease, border-color .18s ease",
                  "&:hover": {
                    color: T.accentHover,
                    borderColor: selectedIssuer
                      ? `${issuerIdentity.accent}52`
                      : T.border,
                    background:
                      "#F8F9FC",
                    boxShadow:
                      "0 4px 12px rgba(15,23,42,.08)",
                    transform: "translateY(-1px)",
                  },
                  "&:active": {
                    transform: "scale(.97)",
                    boxShadow: "0 4px 12px rgba(15,23,42,.08)",
                  },
                  "&:focus-visible": {
                    outline: `3px solid ${T.accentSoft}`,
                    outlineOffset: "2px",
                  },
                  "@media (max-width: 480px)": {
                    width: "42px",
                    minWidth: "42px",
                    padding: 0,
                    gap: 0,
                    fontSize: 0,
                  },
                },
              }}
            />
            <Box
              role="button"
              tabIndex={0}
              aria-label="Open profile settings"
              onClick={openProfile}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  openProfile();
                }
              }}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                cursor: "pointer",
                padding: "6px 10px 6px 6px",
                borderRadius: "14px",
                border: `1px solid ${T.border}`,
                backgroundColor: "rgba(255,255,255,.78)",
                fontFamily: "inherit",
                [MOBILE]: {
                  width: "40px",
                  height: "40px",
                  padding: 0,
                  justifyContent: "center",
                  border: 0,
                  borderRadius: "50%",
                },
                "&:focus-visible": {
                  outline: `2px solid ${T.accent}`,
                  outlineOffset: "2px",
                },
                "&:hover": {
                  backgroundColor: T.surface,
                  boxShadow: "0 4px 12px rgba(15,23,42,.08)",
                },
              }}
            >
              <Box sx={{ display: "flex", [MOBILE]: { display: "none" } }}>
                <Avatar size="34px" />
              </Box>
              <Box
                sx={{
                  display: "none",
                  [MOBILE]: {
                    display: "flex",
                    width: "38px",
                    height: "38px",
                    borderRadius: "50%",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#fff",
                    background: `linear-gradient(135deg, ${T.accent}, #7FA3C6)`,
                    fontSize: "0.78rem",
                    fontWeight: 800,
                  },
                }}
              >
                {profileInitials}
              </Box>
              <VBox sx={{ "@media (max-width: 620px)": { display: "none" } }}>
                <Typo
                  sx={{
                    color: T.text,
                    fontWeight: 700,
                    fontSize: "0.88rem",
                    lineHeight: "1.1",
                  }}
                >
                  {profile?.name || profile?.email}
                </Typo>
                <Typo sx={{ color: T.muted, fontSize: "0.72rem" }}>
                  {profile?.company_role === USER_ROLE.ADMIN
                    ? "Account holder"
                    : profile?.company_role}
                </Typo>
              </VBox>
            </Box>
          </HBox>
        </HBox>

        <Box
          sx={{
            display: "none",
            position: "relative",
            "&::after": {
              content: '""',
              position: "absolute",
              left: "18px",
              right: "18px",
              bottom: 0,
              height: "1px",
              borderRadius: "999px",
              background: selectedIssuer
                ? `linear-gradient(90deg, transparent 0%, rgba(15,23,42,.08) 18%, ${issuerIdentity.accent}28 50%, rgba(15,23,42,.08) 82%, transparent 100%)`
                : "linear-gradient(90deg, transparent, rgba(15,23,42,.1), transparent)",
            },
            [MOBILE]: {
              display: "flex",
              flexShrink: 0,
              minHeight: "54px",
              padding: "7px 12px 9px",
              alignItems: "center",
              justifyContent: "space-between",
              position: "relative",
              background: "transparent",
              borderBottom: 0,
              boxShadow: "none",
              transition: "box-shadow .2s ease",
            },
          }}
        >
          <IssuerSwitcher compact />
          {selectedIssuer && (
            <Typo sx={{ color: T.text, fontSize: "0.8rem", fontWeight: 800, fontVariantNumeric: "tabular-nums" }}>
              {formatCurrencyWithNumberToSymbolWithNumber(selectedIssuer.balance, selectedIssuer.currency)}
            </Typo>
          )}
        </Box>
        </Box>

        {/* Content */}
        <VBox
          ref={contentScrollRef}
          sx={{
            flexGrow: 1,
            minHeight: 0,
            overflowY: "auto",
            overflowX: "hidden",
            overscrollBehavior: "contain",
            padding: "24px 28px 32px",
            [MOBILE]: { padding: "20px 16px 24px" },
          }}
        >
          <Box key={`${scopeKey}:${tab}`} data-page-content sx={{ maxWidth: "1240px", width: "100%", margin: "0 auto" }}>
            {tab === "dashboard" && <PrivateDashboard onNavigate={navigateToTab} />}
            {tab === "cards" && <PrivateCards onTransactions={uid => navigateToTab("transactions", uid)} />}
            {tab === "funding" && <PrivateFunding />}
            {tab === "transactions" && <PrivateTransactions key={transactionCardUid ?? "all"} cardUid={transactionCardUid} onClearCard={() => navigateToTab("transactions")} onTransactions={uid => navigateToTab("transactions", uid)} />}
          </Box>
        </VBox>

        <Box
          role="tablist"
          aria-label="Mobile navigation"
          sx={{
            display: "none",
            [MOBILE]: {
              display: "grid",
              gridTemplateColumns: `repeat(${availableNav.length}, minmax(0, 1fr))`,
              flexShrink: 0,
              padding:
                "6px",
              margin:
                "0 12px calc(8px + var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)))",
              background:
                "rgba(255,255,255,.96)",
              backdropFilter: "blur(24px)",
              border: `1px solid ${T.border}`,
              borderRadius: "22px",
              boxShadow:
                "0 8px 28px rgba(15,23,42,.12)",
            },
          }}
        >
          {availableNav.map((item) => {
            const active = tab === item.id;
            const label = item.id === "cards" ? "Cards" : item.label;
            return (
              <Box
                key={item.id}
                element="button"
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => navigateToTab(item.id)}
                sx={{
                  minWidth: 0,
                  minHeight: "58px",
                  padding: "6px 2px",
                  border: "1px solid transparent",
                  borderRadius: "16px",
                  background: active
                    ? T.accentSoft
                    : "transparent",
                  color: active ? T.accentHover : T.muted,
                  boxShadow: active
                    ? "none"
                    : "none",
                  fontFamily: "inherit",
                  cursor: "pointer",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "3px",
                  transition:
                    "transform .18s ease, color .18s ease, background .18s ease, box-shadow .18s ease",
                  "&:active": { transform: "scale(.96)" },
                  "&:focus-visible": {
                    outline: `2px solid ${T.accent}`,
                    outlineOffset: "-2px",
                  },
                }}
              >
                <span
                  style={{
                    display: "flex",
                    width: "32px",
                    height: "30px",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "11px",
                    background: active ? "#fff" : "transparent",
                    lineHeight: 0,
                    transform: "scale(1.12)",
                  }}
                >
                  {item.icon}
                </span>
                <span
                  style={{
                    color: "inherit",
                    fontSize: "0.72rem",
                    lineHeight: 1.1,
                    fontWeight: active ? 800 : 600,
                    whiteSpace: "nowrap",
                  }}
                >
                  {label}
                </span>
              </Box>
            );
          })}
        </Box>
      </VBox>
    </Box>
  );
};

function IconHome() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
      <path
        d="M4 11l8-6 8 6"
        stroke="currentColor"
        strokeWidth="1.9"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M6 10v9h12v-9"
        stroke="currentColor"
        strokeWidth="1.9"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconCard() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
      <rect
        x="3.5"
        y="6"
        width="17"
        height="12"
        rx="2.2"
        stroke="currentColor"
        strokeWidth="1.9"
      />
      <path d="M3.5 10h17" stroke="currentColor" strokeWidth="1.9" />
      <path
        d="M7 14.5h4"
        stroke="currentColor"
        strokeWidth="1.9"
        strokeLinecap="round"
      />
    </svg>
  );
}

function IconActivity() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
      <path
        d="M4 13h3l2 5 4-12 2 7h5"
        stroke="currentColor"
        strokeWidth="1.9"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconPlus() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
      <path
        d="M12 5v14M5 12h14"
        stroke="currentColor"
        strokeWidth="1.9"
        strokeLinecap="round"
      />
    </svg>
  );
}

function IconLogout() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M10 5H6.8A1.8 1.8 0 0 0 5 6.8v10.4A1.8 1.8 0 0 0 6.8 19H10"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
      <path
        d="M14.5 8.5 18 12l-3.5 3.5M9 12h9"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
